import React, { useContext, useEffect, useState, useCallback } from 'react';
import useStyles from "../../../style/js-style/containers/administration/personalization/personalizationStyle";
import { Box, Tab, Tabs } from '@mui/material';
import i18next from 'i18next';
import EATabPanel from '../../../components/common/EATabPanel';
import {
  COLOR_TYPE,
  FONT_WEIGHT_LIST,
  LOGO_TYPE,
  PALETTE_COLORS,
  TEMPLATE_TYPE,
} from './themeUtils';
import AppearanceSubSection from './appearance/AppearanceSubSection';
import { FormProvider, useForm } from 'react-hook-form';
import Preview from './appearance/Preview';
import { isEmpty } from '../../../services/common/Utils';
import {
  createCompteDonneurDordresTheme,
  getCurrentCompteDonneurDordresTheme,
  updateCompteDonneurDordresTheme,
  updateCompteDonneurDordresThemeLogo,
} from '../../../services/edge/AccountService';
import { ERROR, SUCCESS } from '../../../services/common/Constants';
import { Context } from '../../../states/Store';
import EAButton from '../../../components/common/EAButton';
import { PrivilegeEnum, RoleBasedAccessControlContext } from '../../../services/common/RolesUtils';

function Theme() {
  const { classes } = useStyles();
  const [selectedTab, setSelectedTab] = useState(0);
  const [templateTypes, setTemplateTypes] = useState(TEMPLATE_TYPE.STANDARD);
  const [logoTypes, setLogoTypes] = useState(LOGO_TYPE.IMAGE);
  const [colorTypes, setColorTypes] = useState(COLOR_TYPE.PALETTE);
  const [state, dispatch] = useContext(Context);
  const [uploadedLogo, setUploadedLogo] = useState(null);
  const [preview, setPreview] = useState([]);
  const [hasRemovedLogo, setHasRemovedLogo] = useState(false);
  const handleTabChange = (event, newValue) => setSelectedTab(newValue);
  const { hasAnyOfPrivileges } = React.useContext(RoleBasedAccessControlContext);

  const getFormDefaultValues = () => {
    if (state.personalization && !state.personalization.noFindingTheme) {
      return state.personalization;
    } else {
      return {
        isCustomTemplate: false,
        isCustomColor: false,
        isLogoImage: false,
        logoText: null,
        logoFontWeight: FONT_WEIGHT_LIST[400],
        logoColor: "#000000",
        logoURI: null,
        logoReverseURI: null,
        colorPrimary: PALETTE_COLORS[0]['900'],
        colorHover: PALETTE_COLORS[0]['500'],
        colorBackground: PALETTE_COLORS[0]['50'],
        paletteColorName: "aprovall",
      }
    }
  }

  const formMethods = useForm({
    mode: "onChange",
    delayError: 500,
    defaultValues: getFormDefaultValues(),
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = formMethods;

  const getTemplateItems = useCallback(() => {
    return [
      {
        label: i18next.t("personalization.appearanceTab.standard"),
        value: TEMPLATE_TYPE.STANDARD,
        isAuthorized: hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THEME_STATUS)
      },
      {
        label: i18next.t("personalization.appearanceTab.custom"),
        value: TEMPLATE_TYPE.CUSTOM,
        isAuthorized: hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THEME_STATUS)
      },
    ]
  }, [hasAnyOfPrivileges]);

  const getLogoItems = useCallback(() => {
    return [
      {
        label: i18next.t("personalization.appearanceTab.logo.image"),
        value: LOGO_TYPE.IMAGE,
        isAuthorized: hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THEME_PARAMETERS)
      },
      {
        label: i18next.t("personalization.appearanceTab.logo.texte"),
        value: LOGO_TYPE.TEXTE,
        isAuthorized: hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THEME_PARAMETERS)
      },
    ]
  }, [hasAnyOfPrivileges]);

  const getColorItems = useCallback(() => {
    return [
      {
        label: i18next.t("personalization.appearanceTab.color.palette"),
        value: COLOR_TYPE.PALETTE,
        isAuthorized: hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THEME_PARAMETERS)
      },
      {
        label: i18next.t("personalization.appearanceTab.color.custom"),
        value: COLOR_TYPE.CUSTOM,
        isAuthorized: hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THEME_PARAMETERS)
      },
    ]
  }, [hasAnyOfPrivileges]);

  useEffect(() => {
    if (state.personalization) {
      setTemplateTypes(state.personalization.isCustomTemplate ? TEMPLATE_TYPE.CUSTOM : TEMPLATE_TYPE.STANDARD);
      setLogoTypes(state.personalization.isLogoImage ? LOGO_TYPE.IMAGE : LOGO_TYPE.TEXTE);
      setColorTypes(state.personalization.isCustomColor ? COLOR_TYPE.CUSTOM : COLOR_TYPE.PALETTE);
      setValue('logoURI', state.personalization.logoURI || null)
    }
  }, [state.personalization, setValue]);

  useEffect(() => {
    setValue('isCustomTemplate', templateTypes === TEMPLATE_TYPE.CUSTOM);
    if (!state.personalization.isCustomTemplate && templateTypes === TEMPLATE_TYPE.CUSTOM) {
      setLogoTypes(LOGO_TYPE.IMAGE);
      setValue('paletteColorName', 'aprovall')
    }
  }, [templateTypes, setValue, state.personalization]);

  useEffect(() => {
    setValue('isLogoImage', logoTypes === LOGO_TYPE.IMAGE);
    if (logoTypes === LOGO_TYPE.TEXTE) {
      setPreview([]);
      setUploadedLogo(null)
    }
  }, [logoTypes, setValue]);

  useEffect(() => {
    setValue('isCustomColor', colorTypes === COLOR_TYPE.CUSTOM);
  }, [colorTypes, setValue]);

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  const submitForm = async (data) => {
    if (isEmpty(errors)) {
      try {

        // Création de thème
        if (state.personalization.noFindingTheme) {
          const response = await createCompteDonneurDordresTheme(
            state.account.id,
            data,
            null
          );

          if (response.content.themeOid) {
            if (uploadedLogo && logoTypes === LOGO_TYPE.IMAGE) {
              await updateCompteDonneurDordresThemeLogo(
                state.account.id,
                response.content.themeOid,
                uploadedLogo,
                null
              );
              setUploadedLogo(null);
            }
          }

          // Mise à jour de thème
        } else {
          await updateCompteDonneurDordresTheme(
            state.account.id,
            state.personalization.themeOid,
            data,
            null
          );

          if (uploadedLogo && logoTypes === LOGO_TYPE.IMAGE) {
            await updateCompteDonneurDordresThemeLogo(
              state.account.id,
              state.personalization.themeOid,
              uploadedLogo,
              null
            );
            setUploadedLogo(null);
          }
        }

        // reFetch les données de la personalization
        let result = await getCurrentCompteDonneurDordresTheme(state.account.id, null);
        if (result?.content) {
          dispatch({ type: "PERSONALIZATION", data: result.content })
          dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t('personalization.saveConfirm') } });
        }
      } catch (error) {
        dispatch({
          type: "ALERT",
          alert: {
            type: ERROR,
            msg: error.message?.error?.[0]?.message || "INTERNAL_ERROR",
          },
        });
      }
    }
  }

  return (
    <div className={classes.container}>
      <Box
        className={classes.tabsWrapper}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label="attributs tabs"
        >
          <Tab
            label={i18next.t('personalization.appearance')}
            {...a11yProps(0)}
            className={classes.tab}
          />
        </Tabs>
      </Box>

      <EATabPanel
        value={selectedTab}
        index={0}
        variant={'withoutBackground'}
        style={{ position: 'relative' }}
      >
        <FormProvider {...formMethods}>
          {/*---------------*/}
          {/* Configuration */}
          {/*---------------*/}
          <div className={classes.appearanceContainer}>
            <div className={classes.configurationContainer}>
              {/* Template */}
              <div>
                <AppearanceSubSection
                  title={i18next.t('personalization.appearanceTab.template')}
                  optionsItems={getTemplateItems()}
                  setOption={(templateType) => setTemplateTypes(templateType)}
                  notice={i18next.t(
                    'personalization.appearanceTab.chooseLayout'
                  )}
                  selectedOption={templateTypes}
                  isCustomTemplate={templateTypes === TEMPLATE_TYPE.CUSTOM}
                  type={TEMPLATE_TYPE}
                />
              </div>

              {templateTypes === TEMPLATE_TYPE.CUSTOM && (
                <>
                  {/* Logo */}
                  <AppearanceSubSection
                    title={i18next.t('personalization.appearanceTab.logo.logo')}
                    optionsItems={getLogoItems()}
                    setOption={(logoType) => setLogoTypes(logoType)}
                    notice={i18next.t(
                      'personalization.appearanceTab.logo.chooseLogoType'
                    )}
                    selectedOption={logoTypes}
                    isCustomTemplate={templateTypes === TEMPLATE_TYPE.CUSTOM}
                    setUploadedLogo={(logo) => setUploadedLogo(logo)}
                    hasRemovedLogo={hasRemovedLogo}
                    preview={preview}
                    setHasRemovedLogo={(newValue) =>
                      setHasRemovedLogo(newValue)
                    }
                    setPreview={(newValue) => setPreview(newValue)}
                    type={LOGO_TYPE}
                  />

                  {/* Couleur */}
                  <AppearanceSubSection
                    title={i18next.t(
                      'personalization.appearanceTab.color.color'
                    )}
                    optionsItems={getColorItems()}
                    setOption={(colorType) => setColorTypes(colorType)}
                    notice={i18next.t(
                      'personalization.appearanceTab.color.notice'
                    )}
                    selectedOption={colorTypes}
                    isCustomTemplate={templateTypes === TEMPLATE_TYPE.CUSTOM}
                    type={COLOR_TYPE}
                  />
                </>
              )}
            </div>

            {/*---------*/}
            {/* Preview */}
            {/*---------*/}
            {templateTypes === TEMPLATE_TYPE.CUSTOM && (
              <Preview
                control={control}
                logoTypes={logoTypes}
                hasRemovedLogo={hasRemovedLogo}
                preview={preview}
              />
            )}

            {/*------------*/}
            {/* Sauvegarde */}
            {/*------------*/}
            <EAButton
              onClick={
                !!errors.logoText && logoTypes === LOGO_TYPE.TEXTE
                  ? () => { }
                  : hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THEME_PARAMETERS) && handleSubmit(submitForm)
              }
              customStyle={{
                opacity:
                  ((!!errors.logoText && logoTypes === LOGO_TYPE.TEXTE) || !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THEME_PARAMETERS)) ? 0.6 : 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: state.drawerIsOpened
                  ? 'calc(100% - 255px)'
                  : 'calc(100%)',
                position: 'fixed',
                bottom: '0',
                left: state.drawerIsOpened ? '255px' : '65px',
                right: 0,
                height: '53px',
                borderRadius: '5px',
              }}
              content={i18next.t('personalization.save')}
            />
          </div>
        </FormProvider>
      </EATabPanel>
    </div>
  );
}

export default Theme;
